import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { ITEM_ICON, itemName } from "@/lib/gameData";
import { Coins, Search, X, ArrowUpRight, ArrowDownRight, Package, Flame } from "lucide-react";

function Sparkline({ history, height = 40 }) {
  if (!history || history.length < 2) {
    return <div className="text-xs text-stone-600 italic">Not enough trades yet.</div>;
  }
  const prices = history.map((h) => h.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const w = 220;
  const h = height;
  const span = Math.max(1, max - min);
  const step = w / (prices.length - 1);
  const pts = prices.map((p, i) => `${i * step},${h - ((p - min) / span) * h}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" data-testid="ge-sparkline">
      <polyline fill="none" stroke="#f59e0b" strokeWidth="1.5" points={pts} />
    </svg>
  );
}

function ClaimBoxWidget() {
  const { snap, content, geClaim } = useGame();
  const cb = snap?.claim_box || { items: [], gold: 0 };
  const has = (cb.items || []).length > 0 || (cb.gold || 0) > 0;

  async function claim() {
    try {
      const res = await geClaim();
      const parts = [];
      if (res.gold_added > 0) parts.push(`${res.gold_added.toLocaleString()} gp`);
      if (res.added?.length) parts.push(`${res.added.length} stack(s)`);
      toast.success(parts.length ? `Claimed ${parts.join(" + ")}.` : "Claim box empty.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="panel p-4" data-testid="ge-claim-box">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg heading-gilded">Claim box</h3>
        <button
          onClick={claim}
          disabled={!has}
          className="btn-gold text-sm px-3 py-1 disabled:opacity-40"
          data-testid="ge-claim-btn"
        >
          Claim all
        </button>
      </div>
      {has ? (
        <div className="space-y-2 text-sm font-body">
          {cb.gold > 0 && (
            <div className="flex items-center gap-2 text-amber-300">
              <Coins size={14} /> <span className="font-numeric">{cb.gold.toLocaleString()} gp</span>
            </div>
          )}
          {(cb.items || []).map((slot, i) => (
            <div key={i} className="flex items-center gap-2 text-stone-200" data-testid={`ge-claim-item-${slot.item}`}>
              <span className="text-lg">{ITEM_ICON[slot.item] || "◇"}</span>
              <span className="font-numeric text-emerald-300">×{slot.qty}</span>
              <span>{itemName(slot.item, content?.items)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-stone-500 italic text-sm font-body">Nothing awaiting.</div>
      )}
    </div>
  );
}

function MyOrders() {
  const { geCancel } = useGame();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/ge/orders/mine");
      setOrders(data.orders || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 4000);
    return () => clearInterval(iv);
  }, [refresh]);

  async function cancel(id) {
    try { await geCancel(id); toast.message("Order cancelled."); await refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="panel p-4" data-testid="ge-my-orders">
      <h3 className="font-display text-lg heading-gilded mb-3">My orders</h3>
      {loading ? (
        <div className="text-stone-500 text-sm italic">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="text-stone-500 italic text-sm font-body">No active orders.</div>
      ) : (
        <div className="space-y-1.5">
          {orders.map((o) => (
            <div
              key={o.id}
              className={`flex items-center gap-2 text-sm font-body px-2 py-1 rounded border ${
                o.status === "open" ? "border-stone-700" : "border-stone-800 opacity-60"
              }`}
              data-testid={`ge-order-${o.id}`}
            >
              <span className="text-lg">{ITEM_ICON[o.item_id] || "◇"}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={o.side === "buy" ? "text-emerald-400" : "text-red-400"}>
                    {o.side.toUpperCase()}
                  </span>
                  <span className="text-stone-200">{o.item_id.replace(/_/g, " ")}</span>
                </div>
                <div className="text-xs font-numeric text-stone-500">
                  {o.qty_remaining}/{o.qty_total} @ {o.price_per_unit.toLocaleString()}gp — {o.status}
                </div>
              </div>
              {o.status === "open" && (
                <button
                  onClick={() => cancel(o.id)}
                  className="text-stone-500 hover:text-red-400 p-1"
                  title="Cancel"
                  data-testid={`ge-cancel-${o.id}`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderBook({ itemId, book, onRefresh }) {
  const { snap, gePlaceOrder } = useGame();
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(0);

  const meta = ITEM_ICON[itemId];
  const best_sell = book?.sells?.[0]?.price_per_unit;
  const best_buy = book?.buys?.[0]?.price_per_unit;

  useEffect(() => {
    // Pre-fill price with best counter-side, if any
    if (side === "buy" && best_sell) setPrice(best_sell);
    else if (side === "sell" && best_buy) setPrice(best_buy);
  }, [itemId, side, best_sell, best_buy]);

  async function submit(e) {
    e.preventDefault();
    if (qty <= 0 || price <= 0) {
      toast.error("Quantity and price must be positive.");
      return;
    }
    try {
      const order = await gePlaceOrder({ side, item_id: itemId, qty: Number(qty), price_per_unit: Number(price) });
      toast.success(
        order.status === "filled"
          ? "Order fully filled!"
          : order.qty_remaining < order.qty_total
            ? `Partially filled — ${order.qty_total - order.qty_remaining}/${order.qty_total}`
            : "Order placed."
      );
      setQty(1);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="panel p-4 space-y-4" data-testid="ge-orderbook">
      {/* Book */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-500 font-numeric mb-1">Buy orders</div>
          <div className="space-y-1 max-h-40 overflow-y-auto scroll-thin">
            {(book?.buys || []).length === 0 && (
              <div className="text-stone-600 italic text-xs">None</div>
            )}
            {(book?.buys || []).map((o) => (
              <div key={o.id} className="flex justify-between text-xs font-numeric text-stone-300" data-testid={`book-buy-${o.id}`}>
                <span className="text-emerald-400">{o.price_per_unit.toLocaleString()}</span>
                <span>×{o.qty_remaining}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-red-500 font-numeric mb-1">Sell orders</div>
          <div className="space-y-1 max-h-40 overflow-y-auto scroll-thin">
            {(book?.sells || []).length === 0 && (
              <div className="text-stone-600 italic text-xs">None</div>
            )}
            {(book?.sells || []).map((o) => (
              <div key={o.id} className="flex justify-between text-xs font-numeric text-stone-300" data-testid={`book-sell-${o.id}`}>
                <span className="text-red-400">{o.price_per_unit.toLocaleString()}</span>
                <span>×{o.qty_remaining}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Price chart */}
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-numeric mb-1">Recent price history</div>
        <Sparkline history={book?.history || []} />
      </div>

      {/* Place order */}
      <form onSubmit={submit} className="space-y-2 pt-2 border-t border-stone-800" data-testid="ge-place-form">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSide("buy")}
            className={"flex-1 py-1.5 rounded border text-sm font-body flex items-center justify-center gap-1 " +
              (side === "buy"
                ? "bg-emerald-900/40 border-emerald-500 text-emerald-200"
                : "bg-stone-900 border-stone-700 text-stone-400")}
            data-testid="ge-side-buy"
          >
            <ArrowDownRight size={14} /> Buy
          </button>
          <button
            type="button"
            onClick={() => setSide("sell")}
            className={"flex-1 py-1.5 rounded border text-sm font-body flex items-center justify-center gap-1 " +
              (side === "sell"
                ? "bg-red-900/40 border-red-500 text-red-200"
                : "bg-stone-900 border-stone-700 text-stone-400")}
            data-testid="ge-side-sell"
          >
            <ArrowUpRight size={14} /> Sell
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-numeric">Qty</span>
            <input
              type="number" min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-numeric text-amber-300 focus:border-amber-600 outline-none"
              data-testid="ge-qty"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-numeric">Price / unit (gp)</span>
            <input
              type="number" min="1"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-numeric text-amber-300 focus:border-amber-600 outline-none"
              data-testid="ge-price"
            />
          </label>
        </div>
        <div className="flex items-center justify-between text-xs text-stone-500 font-numeric">
          <span>Est. total: <span className="text-amber-300">{(qty * price).toLocaleString()} gp</span></span>
          <span>Tax on fill: 2%</span>
        </div>
        <button type="submit" className="btn-gold w-full py-1.5 text-sm" data-testid="ge-submit">
          Place {side} order
        </button>
      </form>
    </div>
  );
}

export function GrandExchangePanel() {
  const { content, snap } = useGame();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [book, setBook] = useState(null);
  const [metrics, setMetrics] = useState({ total_tax: 0 });

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/ge/items");
      setItems(data.items || []);
      if (!selected && data.items?.[0]) setSelected(data.items[0].id);
    } catch (e) { /* noop */ }
  }, [selected]);

  const loadBook = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/api/game/ge/book/${id}`);
      setBook(data);
    } catch (e) { /* noop */ }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/ge/metrics");
      setMetrics(data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadItems(); loadMetrics(); }, [loadItems, loadMetrics]);
  useEffect(() => {
    loadBook(selected);
    if (!selected) return undefined;
    const iv = setInterval(() => loadBook(selected), 4000);
    return () => clearInterval(iv);
  }, [selected, loadBook]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return items.filter((it) => it.name.toLowerCase().includes(q));
  }, [items, filter]);

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="ge-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl heading-gilded mb-1">Grand Exchange</h2>
          <p className="text-stone-500 font-body">
            Asynchronous order-book market. Escrow-backed, 2% tax funds the realm&apos;s coffers.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs font-numeric text-stone-400">
          <Flame size={14} className="text-red-400" />
          Gold burned by tax so far:
          <span className="text-amber-300 font-semibold" data-testid="ge-gold-sink">
            {metrics.total_tax.toLocaleString()} gp
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Item list */}
        <div className="lg:col-span-4 panel p-3 flex flex-col h-[560px]">
          <div className="flex items-center gap-2 mb-2">
            <Search size={14} className="text-stone-500" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search items…"
              className="flex-1 bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-body focus:border-amber-600 outline-none"
              data-testid="ge-search"
            />
          </div>
          <div className="flex-1 overflow-y-auto scroll-thin space-y-0.5 pr-1" data-testid="ge-item-list">
            {filtered.map((it) => {
              const active = selected === it.id;
              return (
                <button
                  key={it.id}
                  onClick={() => setSelected(it.id)}
                  data-testid={`ge-item-${it.id}`}
                  className={
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm font-body transition-colors duration-150 " +
                    (active
                      ? "bg-amber-900/40 border border-amber-700 text-amber-200"
                      : "hover:bg-stone-800/60 border border-transparent text-stone-300")
                  }
                >
                  <span className="text-lg">{ITEM_ICON[it.id] || "◇"}</span>
                  <span className="flex-1 truncate">{it.name}</span>
                  <span className="font-numeric text-xs text-stone-500">
                    {it.last_price != null ? `${it.last_price}gp` : "—"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Order book + form */}
        <div className="lg:col-span-5">
          {selected ? (
            <>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{ITEM_ICON[selected] || "◇"}</span>
                <div>
                  <div className="font-display text-xl heading-gilded" data-testid="ge-selected-name">
                    {content?.items?.[selected]?.name}
                  </div>
                  <div className="text-[11px] text-stone-500 font-numeric uppercase tracking-widest">
                    {content?.items?.[selected]?.cat}
                  </div>
                </div>
              </div>
              <OrderBook itemId={selected} book={book} onRefresh={() => loadBook(selected)} />
            </>
          ) : (
            <div className="panel p-8 text-center text-stone-500 italic">
              Pick an item to view its market.
            </div>
          )}
        </div>

        {/* Right column: my orders + claim */}
        <div className="lg:col-span-3 space-y-4">
          <ClaimBoxWidget />
          <MyOrders />
        </div>
      </div>

      <p className="text-xs text-stone-500 font-body italic flex items-center gap-2">
        <Package size={12} /> Bought items and refunds land in your Claim box. Sweep them into your inventory anytime.
      </p>
    </div>
  );
}
